import { BlockError } from '@/blocks.04-project/block-error/block-error';
import { LazyInit } from '@/blocks.04-project/lazy-init/lazy-init';
import toArray from 'lodash-es/toArray';
import get from 'lodash-es/get';
import findIndex from 'lodash-es/findIndex';

/**
 * Класс библиотеки light-gallery
 * @example
 *
 * Атрибуты:
 *   [data-iv-toggle="gallery"] - инициализирует все элементы с этим атрибутом
 *   [data-gallery-target] - атрибут, который добавляет пользовательское селекторы вместо прямых дочерних элементов.
 *   [data-exthumbimage] - src для картинки в thumbnail
 *   [data-gallery-show-video] - аттрибут, который указывает что при открытии галереи должно открыться видео
 */
class LightGallery {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент формы с аттрибутом [data-iv-toggle="gallery"]
   */
  constructor(elem, force) {
    this.BlockError = new BlockError({ blockName: 'light-gallery', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.LightGallery = this;
    this.block.AsyncLightGallery = new Promise((resolve) => {
      this.promiseResolve = resolve;
    }).then((ins) => ins);

    this.LazyInit = new LazyInit({ ins: this, callback: this.init, addClass: 'light-gallery_inited' });
    this.LazyInit.init();
    if (force) this.LazyInit.earlyInit();
  }

  init = async () => {
    const { LGallery, Plugins } = await Promise.all([
      import(/* webpackChunkName: "LightGallery" */ 'lightgallery'),
      import(/* webpackChunkName: "LightGallery" */ 'lightgallery/plugins/thumbnail'),
      import(/* webpackChunkName: "LightGallery" */ 'lightgallery/plugins/video'),
    ]).then((data) => {
      return {
        LGallery: data[0]?.default,
        Plugins: toArray(data[1]),
      };
    }).catch((err) => {
      throw new Error(err);
    });
    this.LGallery = LGallery;
    this.Plugins = Plugins;

    this.targets = this.block.querySelectorAll('[data-gallery-target], [data-toggle="gallery"]');
    this.isMoreOne = this.targets.length > 1;

		this.block.addEventListener('lgInit', (event) => {
			const lg = event.detail.instance;
			const items = toArray(get(lg, 'items'));
			const container = get(lg, '$container');
			const images = toArray(get(container, 'firstElement')?.querySelectorAll('.lg-thumb-item img'));
			for (let i = 0; i < images.length; i++) {
				images[i].classList.add('lazyload');
				const src = items[i]?.dataset['srcThumb'] || images[i]?.src;
				images[i].setAttribute('data-src', src);
			}
		}, {once: true});

    this.gallery = this.LGallery(this.block, {
      // [data-toggle="gallery"] - поддержка старого кода на беке
      selector: '[data-gallery-target], [data-toggle="gallery"]',
      plugins: this.Plugins,
      thumbnail: true,
      showThumbByDefault: true,
      toggleThumb: false,
      thumbWidth: 100,
      thumbMargin: 20,
      enableThumbDrag: true,
      animateThumb: true,
      allowMediaOverlap: true,
      counter: false,
      download: false,
      exThumbImage: 'data-exthumbimage',
      getCaptionFromTitleOrAlt: false,
      controls: this.isMoreOne,
      mobileSettings: {
        thumbWidth: 100,
        thumbMargin: 10,
      },
    });

    // Установка размеров для картинок
    this.block.addEventListener('lgAfterAppendSlide', (event) => {
      const items = get(this.gallery, 'items', []);
      const index = get(event, 'detail.index', 0);
      const item = get(items, index);
      const id = get(this.gallery, 'lgId');
      const galleryItem = document.getElementById(`lg-item-${id}-${index}`);
      const galleryVideo = galleryItem?.querySelector('.lg-video-cont');
      if (!item || !galleryVideo) return;

      const width = item.dataset['width'] || 1;
      const height = item.dataset['height'] || 1;

      galleryVideo.style.setProperty('--width', width);
      galleryVideo.style.setProperty('--height', height);
    });

    this.block.addEventListener('click', this.onClick);

    this.promiseResolve(this);
    this.block.AsyncLightGallery = this;
  };

  onClick = (event) => {
    if (event.target.closest('[data-gallery-show-video]')) {
      const indexVideo = findIndex(this.gallery.galleryItems, (item) => item.iframe === 'true');
      if (indexVideo >= 0) {
        this.gallery.slide(indexVideo);
      }
    }
  };

  destroy = () => {
    if (this.gallery) this.gallery.destroy();
    if (this.block) {
      this.block.removeEventListener('click', this.onClick);
      this.block.classList.remove('light-gallery_inited');
      delete this.block.LightGallery;
      delete this.block.AsyncLightGallery;
    }
  };

  static init(container = document, force) {
    Array.from(container.querySelectorAll('[data-iv-toggle="gallery"]:not(.light-gallery_inited)')).forEach((elem) => new LightGallery(elem, force));
  }
}

document.addEventListener('DOMContentLoaded', () => LightGallery.init());

// Экспорт;
window.Block.Lightgallery = LightGallery;
